import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-hot-toast';

export type AutoSaveStatus = 'idle' | 'saving' | 'saved' | 'error' | 'offline';

export interface AutoSaveState {
  status: AutoSaveStatus;
  lastSaved?: Date;
  error?: string;
  hasUnsavedChanges: boolean;
}

export interface UseAutoSaveOptions<T> {
  data: T;
  onSave: (data: T) => Promise<void>;
  delay?: number; // ms para debounce
  enabled?: boolean;
  compareFunction?: (prev: T, current: T) => boolean;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  showToast?: boolean;
}

export function useAutoSave<T>({
  data,
  onSave,
  delay = 2000,
  enabled = true,
  compareFunction,
  onSuccess,
  onError,
  showToast = false
}: UseAutoSaveOptions<T>) {
  const [state, setState] = useState<AutoSaveState>({
    status: 'idle',
    hasUnsavedChanges: false
  });

  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const previousDataRef = useRef<T>(data);
  const savingRef = useRef(false);

  // Função para comparar dados
  const hasDataChanged = useCallback((prev: T, current: T): boolean => {
    if (compareFunction) {
      return compareFunction(prev, current);
    }
    // Comparação padrão (shallow)
    return JSON.stringify(prev) !== JSON.stringify(current);
  }, [compareFunction]);

  // Função para salvar dados
  const saveData = useCallback(async (dataToSave: T) => {
    if (savingRef.current) return;
    
    savingRef.current = true;
    setState(prev => ({ ...prev, status: 'saving' }));

    try {
      await onSave(dataToSave);
      
      setState(prev => ({
        ...prev,
        status: 'saved',
        lastSaved: new Date(),
        hasUnsavedChanges: false,
        error: undefined
      }));

      previousDataRef.current = dataToSave;
      
      if (showToast) {
        toast.success('Alterações salvas automaticamente', {
          duration: 2000,
          icon: '💾'
        });
      }
      
      onSuccess?.();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro ao salvar';
      
      setState(prev => ({
        ...prev,
        status: 'error',
        error: errorMessage,
        hasUnsavedChanges: true
      }));

      if (showToast) {
        toast.error('Erro ao salvar automaticamente');
      }
      
      onError?.(error instanceof Error ? error : new Error(errorMessage));
    } finally {
      savingRef.current = false;
    }
  }, [onSave, onSuccess, onError, showToast]);

  // Função para trigger manual do save
  const triggerSave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    saveData(data);
  }, [data, saveData]);

  // Efeito para detectar mudanças e agendar salvamento
  useEffect(() => {
    if (!enabled) return;

    const hasChanged = hasDataChanged(previousDataRef.current, data);
    
    if (hasChanged) {
      setState(prev => ({ ...prev, hasUnsavedChanges: true, status: 'idle' }));
      
      // Clear timeout anterior
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Agendar novo salvamento
      timeoutRef.current = setTimeout(() => {
        saveData(data);
      }, delay);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [data, enabled, delay, hasDataChanged, saveData]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Funções de controle
  const resetState = useCallback(() => {
    setState({
      status: 'idle',
      hasUnsavedChanges: false
    });
    previousDataRef.current = data;
  }, [data]);

  const retry = useCallback(() => {
    if (state.status === 'error') {
      saveData(data);
    }
  }, [state.status, data, saveData]);

  return {
    state,
    triggerSave,
    resetState,
    retry
  };
}